import { LocalDate } from '@js-joda/core';
import { useCallback, useMemo, useState } from 'react';
import { useSessionStorage } from 'react-use';

import { AvailabilitySimple, AvailabilitySimpleQuery } from '@dreryk/egabinet-appointment-frontend-api';
import { DoctorSimple } from '@dreryk/egabinet-personnel-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import { UseCqrsQueryResult, useCqrsSearch } from '@dreryk/command-query-react-common';
import { paginations } from '@dreryk/command-query-ws-client';
import { TimetableEvent } from '@dreryk/react-calendar';
import { dispatchSnackbarWarningEvent } from '@dreryk/react-components';
import { Range } from '@dreryk/watermelon';

import { personnelTranslationKeys } from '../translations';

import { WEEK_START_DAY } from './common/common.utils';
import { SELECTED_DOCTOR_ID_STORAGE_KEY } from './DoctorSelect';

export const useAvailabilitiesSearch = (
   dateRange: Range<Date>,
   practitionerId: PractitionerId
): UseCqrsQueryResult<AvailabilitySimple[]> => {
   return useCqrsSearch(() => {
      const query = new AvailabilitySimpleQuery();
      query.dateRange = dateRange;
      query.practitionerIds = [practitionerId];
      return query;
   }, paginations.all());
};

export const useUpdateModal = (): {
   onEventClick: (event: TimetableEvent<AvailabilitySimple>) => void;
   selectedAvailability?: AvailabilitySimple;
   closeModal: () => void;
} => {
   const [selectedAvailability, setSelectedAvailability] = useState<AvailabilitySimple>();

   const closeModal = (): void => {
      setSelectedAvailability(undefined);
   };

   const eventClickHandler = (event: TimetableEvent<AvailabilitySimple>): void => {
      const eventRecurring = event.data.recurring;
      const eventStartTime = event.data.availabilityDate;

      if (!eventRecurring && eventStartTime.isBefore(LocalDate.now())) {
         dispatchSnackbarWarningEvent(personnelTranslationKeys.incorrectPastDateForSingleAvailabilityUpdate);
         return;
      }
      if (eventRecurring && eventStartTime.isBefore(LocalDate.now().with(WEEK_START_DAY))) {
         dispatchSnackbarWarningEvent(personnelTranslationKeys.incorrectPastDateForRecurringAvailabilityUpdate);
         return;
      }
      setSelectedAvailability(event.data);
   };
   return {
      closeModal: closeModal,
      selectedAvailability: selectedAvailability,
      onEventClick: eventClickHandler,
   };
};

type UseSelectedDoctorStateReturn = [selectedDoctor: DoctorSimple, handleDoctorSelect: (doctor: DoctorSimple) => void];

export const useSelectedDoctorState = (
   initialDoctor: DoctorSimple | null,
   doctors: DoctorSimple[]
): UseSelectedDoctorStateReturn => {
   const [storedDoctorId, setStoredDoctorId] = useSessionStorage<number | undefined>(SELECTED_DOCTOR_ID_STORAGE_KEY);

   const [selectedDoctor, setSelectedDoctor] = useState<DoctorSimple>(() => {
      const getSelectedDoctorFromSessionStorage = (): DoctorSimple | undefined => {
         return doctors.find((doctor) => doctor.id === storedDoctorId);
      };

      const getCurrentDoctor = (): DoctorSimple | undefined => {
         return doctors.find((doctor) => doctor.id === initialDoctor?.id);
      };

      const getFirstDoctor = (): DoctorSimple => {
         return doctors[0];
      };

      return getSelectedDoctorFromSessionStorage() ?? getCurrentDoctor() ?? getFirstDoctor();
   });

   const handleDoctorSelect = useCallback(
      (doctor: DoctorSimple) => {
         setSelectedDoctor(doctor);
         setStoredDoctorId(doctor.id);
      },
      [setStoredDoctorId]
   );
   return useMemo(() => [selectedDoctor, handleDoctorSelect], [selectedDoctor, handleDoctorSelect]);
};
